// src/Payroll_Management/PayRoll_Report/EmployeeLicenseRenewalReport.jsx
import { useEffect, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  IdCard,
  Search,
  RefreshCcw,
  Printer,
  StickyNote,
  Building2,
  Users,
  Layers,
  Briefcase,
  ShieldCheck,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ---------------- helpers ----------------




function toStr(v) {
  return (v ?? "").toString();
}
function s(v) {
  return toStr(v).trim();
}
function safeCsv(v) {
  const t = (v ?? "").toString();
  return `"${t.replaceAll('"', '""')}"`;
}
function useOutsideClick(ref, onOutside) {
  useEffect(() => {
    function handler(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onOutside?.();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

// debounce without libraries
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---------------- component ----------------
const EmployeeLicenseRenewalReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchIdLogin = localStorage.getItem("branchId") || ""; // not used in SP, but ok to keep
  const branchType = localStorage.getItem("branchType") || "HO";

  // meta
  const [meta, setMeta] = useState({
    branches: [{ id: "Select", name: "Select" }],
    departments: [{ id: "Select", name: "Select" }],
    designations: [{ id: "Select", name: "Select" }],
    grades: [{ id: "Select", name: "Select" }],
  });

  // filters (ASP defaults are "Select")
  const [listBranch, setListBranch] = useState("Select");
  const [dept, setDept] = useState("Select");
  const [grade, setGrade] = useState("Select");
  const [desig, setDesig] = useState("Select");
  const [pfNonPf, setPfNonPf] = useState("Select");

  // autocomplete inputs
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");

  // name suggestions
  const [nameSug, setNameSug] = useState([]);
  const [nameOpen, setNameOpen] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);

  // code suggestions
  const [codeSug, setCodeSug] = useState([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  const nameBoxRef = useRef(null);
  const codeBoxRef = useRef(null);
  useOutsideClick(nameBoxRef, () => setNameOpen(false));
  useOutsideClick(codeBoxRef, () => setCodeOpen(false));

  const debEmpName = useDebouncedValue(empName, 250);
  const debEmpCode = useDebouncedValue(empCode, 250);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : (Employee License Renewal Report)", []);

  // styles
  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const badge =
    `px-4 py-2 rounded-2xl text-sm font-bold ` +
    (isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800");

  const pill =
    `px-4 py-2 rounded-2xl text-sm font-bold ` +
    (isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800");

  // ---------------- API ----------------
  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/meta`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success) {
        setMeta({
          branches: res.data.branches || [{ id: "Select", name: "Select" }],
          departments: res.data.departments || [{ id: "Select", name: "Select" }],
          designations: res.data.designations || [{ id: "Select", name: "Select" }],
          grades: res.data.grades || [{ id: "Select", name: "Select" }],
        });
      } else {
        toast.error(res?.data?.message || "Failed to load dropdowns.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchType).trim()) {
      toast.warn("Missing branchType in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/data`, {
        params: {
          finYearId: Number(finYearId),
          branchType: String(branchType),

          listBranch,
          desig,
          dept,
          grade,
          pfNonPf,

          empName: empName.trim(),
          empCode: empCode.trim(),

          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  // show only 5 page numbers (sliding window)
const maxPageButtons = 5;

const getPageWindow = (current, totalPages, maxBtns) => {
  if (totalPages <= maxBtns) {
    return { start: 1, end: totalPages };
  }

  // sliding window: keep current in middle when possible
  const half = Math.floor(maxBtns / 2); // for 5 => 2
  let start = current - half;
  let end = current + half;

  if (start < 1) {
    start = 1;
    end = maxBtns;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - maxBtns + 1;
  }
  return { start, end };
};

const { start: pageStart, end: pageEnd } = getPageWindow(page, totalPages, maxPageButtons);
const pageNumbers = Array.from({ length: pageEnd - pageStart + 1 }, (_, i) => pageStart + i);

  const fetchNameSuggestions = async (q) => {
    if (!String(finYearId).trim() || !String(branchType).trim()) return;
    if (!q || q.length < 1) {
      setNameSug([]);
      return;
    }

    try {
      setNameLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/autocomplete/name`, {
        params: {
          finYearId: Number(finYearId),
          branchId: listBranch, // ASP uses ddl_listbranch selected value
          branchType: String(branchType),
          q,
        },
      });

      if (res?.data?.success) setNameSug(res.data.items || []);
      else setNameSug([]);
    } catch {
      setNameSug([]);
    } finally {
      setNameLoading(false);
    }
  };

  const fetchCodeSuggestions = async (q) => {
    if (!String(finYearId).trim() || !String(branchType).trim()) return;
    if (!q || q.length < 1) {
      setCodeSug([]);
      return;
    }

    try {
      setCodeLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/autocomplete/code`, {
        params: {
          finYearId: Number(finYearId),
          branchId: listBranch,
          branchType: String(branchType),
          q,
        },
      });

      if (res?.data?.success) setCodeSug(res.data.items || []);
      else setCodeSug([]);
    } catch {
      setCodeSug([]);
    } finally {
      setCodeLoading(false);
    }
  };

  const fetchPrintAllData = async () => {
    const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/print-data`, {
      params: {
        finYearId: Number(finYearId),
        branchType: String(branchType),
        listBranch,
        desig,
        dept,
        grade,
        pfNonPf,
        empName: empName.trim(),
        empCode: empCode.trim(),
      },
    });
    return res?.data?.rows || [];
  };

  // -------------- effects --------------
  useEffect(() => {
    fetchMeta();
    // initial list (like ASP)
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    // name suggestions
    if (!nameOpen) return;
    fetchNameSuggestions(debEmpName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpName, nameOpen, listBranch]);

  useEffect(() => {
    // code suggestions
    if (!codeOpen) return;
    fetchCodeSuggestions(debEmpCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpCode, codeOpen, listBranch]);

  // -------------- actions --------------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleClear = async () => {
    setEmpName("");
    setEmpCode("");
    setListBranch("Select");
    setDept("Select");
    setGrade("Select");
    setDesig("Select");
    setPfNonPf("Select");
    setPage(1);
    await fetchList(1);
  };

  const handleExcel = async () => {
    try {
      if (!String(finYearId).trim()) {
        toast.warn("Please select Financial Year.");
        return;
      }
      if (!String(branchType).trim()) {
        toast.warn("Missing branchType in localStorage.");
        return;
      }

      const data = await fetchPrintAllData();
      if (!data.length) {
        toast.info("No records to export");
        return;
      }

      // ASP columns
      const headers = [
        "S.No",
        "Employee Code",
        "Employee Name",
        "Designation",
        "Driving License No",
        "DL Expiry Date",
        "Other Expiry Date",
        "Branch Name",
      ];

      const lines = [headers.join(",")];
      data.forEach((r, idx) => {
        lines.push(
          [
            idx + 1,
            safeCsv(r.fld_employeecode),
            safeCsv(r.fld_Employee),
            safeCsv(r.fld_designationname),
            safeCsv(r.fld_drivinglicnum),
            safeCsv(r.fld_drivinglicexpdate),
            safeCsv(r.fld_otherexpirydate),
            safeCsv(r.fld_BranchName),
          ].join(",")
        );
      });

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Employee_License_Renewal_Report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Exported successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Export failed");
    }
  };

  const handlePrint = () => {
    // Like ASP: opens EmployeeLicenseReportPrint.aspx with sessions.
    // React: open a route with query params; your print page can call /print-data.
    const qs = new URLSearchParams({
      listBranch,
      grade,
      dept,
      desig,
      pfNonPf,
      empCode: empCode.trim(),
      empName: empName.trim(),
      branchType: String(branchType),
    }).toString();

    window.open(`/PayRoll/EmployeeLicenseRenewalReport_Print?${qs}`, "_blank", "width=900,height=900");
  };

  const openRemarks = (employeeId) => {
    // ASP sets Session["LicReportEmployeeID"] then opens LicenseRemarksReportListing.aspx
    // React: pass id in query param
    navigate(
      `/PayRoll/LicenseRemarksReportListing?employeeId=${encodeURIComponent(String(employeeId))}`
    );
  };

  // counts
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  // ---------------- UI ----------------
  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE LICENSE RENEWAL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <IdCard className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Report Filters & Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Pagination / Print / Remarks
                </div>
              </div>

              <div className={badge}>{screenBadge}</div>
              <div className={pill}>{total} Records</div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleExcel} className={ghostBtn} disabled={loading}>
                <Layers className="w-4 h-4" />
                Excel
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Name autocomplete */}
              <div ref={nameBoxRef} className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => {
                      setEmpName(e.target.value);
                      setNameOpen(true);
                    }}
                    onFocus={() => setNameOpen(true)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {nameOpen && (nameLoading || nameSug.length > 0) && (
                  <div
                    className={`absolute z-50 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {nameLoading ? (
                      <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Loading...
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-auto">
                        {nameSug.slice(0, 10).map((item, idx) => (
                          <button
                            type="button"
                            key={`${item}-${idx}`}
                            onClick={() => {
                              setEmpName(item);
                              setNameOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm transition ${
                              isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                            }`}
                          >
                            {item}
                          </button>
                        ))}
                        {nameSug.length === 0 && (
                          <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            No matches
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Employee Code autocomplete */}
              <div ref={codeBoxRef} className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <ShieldCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => {
                      setEmpCode(e.target.value);
                      setCodeOpen(true);
                    }}
                    onFocus={() => setCodeOpen(true)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {codeOpen && (codeLoading || codeSug.length > 0) && (
                  <div
                    className={`absolute z-50 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {codeLoading ? (
                      <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Loading...
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-auto">
                        {codeSug.slice(0, 10).map((item, idx) => (
                          <button
                            type="button"
                            key={`${item}-${idx}`}
                            onClick={() => {
                              setEmpCode(item);
                              setCodeOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm transition ${
                              isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                            }`}
                          >
                            {item}
                          </button>
                        ))}
                        {codeSug.length === 0 && (
                          <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            No matches
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 flex items-center gap-2">
                  <Building2 className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={listBranch}
                    onChange={(e) => setListBranch(e.target.value)}
                    className={inputBase}
                  >
                    {(meta.branches || []).map((b) => (
                      <option key={String(b.id)} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={dept} onChange={(e) => setDept(e.target.value)} className={inputBase}>
                    {(meta.departments || []).map((d) => (
                      <option key={String(d.id)} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Grade */}
              <div>
                <label className={labelBase}>Grade</label>
                <div className="mt-2 flex items-center gap-2">
                  <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={grade} onChange={(e) => setGrade(e.target.value)} className={inputBase}>
                    {(meta.grades || []).map((g) => (
                      <option key={String(g.id)} value={String(g.id)}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Designation */}
              <div>
                <label className={labelBase}>Designation</label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={desig} onChange={(e) => setDesig(e.target.value)} className={inputBase}>
                    {(meta.designations || []).map((d) => (
                      <option key={String(d.id)} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PF / Non PF */}
              <div>
                <label className={labelBase}>PF / Non PF</label>
                <div className="mt-2 flex items-center gap-2">
                  <ShieldCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={pfNonPf} onChange={(e) => setPfNonPf(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button type="button" onClick={handleClear} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                Clear
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">EMP Code</th>
                    <th className="px-4 py-4 text-center font-bold">EMP Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Driving License No.</th>
                    <th className="px-4 py-4 text-center font-bold">Driving License Expiry Date</th>
                    <th className="px-4 py-4 text-center font-bold">Other Expiry Date</th>
                    <th className="px-4 py-4 text-center font-bold">BranchName</th>
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 9 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_employeeid ?? r.fld_employeecode ?? idx}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_Employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_Employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_drivinglicnum}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_drivinglicexpdate}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_otherexpirydate}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_BranchName}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => openRemarks(r.fld_employeeid)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transform hover:-translate-y-0.5 transition-all shadow"
                          >
                            <StickyNote className="w-4 h-4" />
                            Remarks
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

           {totalPages > 1 && (
  <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
    <button
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      disabled={page === 1}
      className={`px-4 py-2 text-sm font-bold ${
        page === 1
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      Prev
    </button>

    {/* optional jump back */}
    <button
      onClick={() => setPage(1)}
      disabled={page === 1}
      className={`px-4 py-2 text-sm font-bold ${
        page === 1
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
      title="First"
    >
      «
    </button>

    {pageStart > 1 && (
      <button
        onClick={() => setPage(pageStart - 1)}
        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        title="Previous pages"
      >
        ...
      </button>
    )}

    {pageNumbers.map((n) => (
      <button
        key={n}
        onClick={() => setPage(n)}
        className={`px-4 py-2 text-sm font-bold transition ${
          page === n
            ? `${primaryBtnClass} text-white`
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        }`}
      >
        {n}
      </button>
    ))}

    {pageEnd < totalPages && (
      <button
        onClick={() => setPage(pageEnd + 1)}
        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        title="Next pages"
      >
        ...
      </button>
    )}

    {/* optional jump forward */}
    <button
      onClick={() => setPage(totalPages)}
      disabled={page === totalPages}
      className={`px-4 py-2 text-sm font-bold ${
        page === totalPages
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
      title="Last"
    >
      »
    </button>

    <button
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      disabled={page === totalPages}
      className={`px-4 py-2 text-sm font-bold ${
        page === totalPages
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      Next
    </button>
  </div>
)}

          </div>

          {/* tiny debug */}
          <div className={`mt-3 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            BranchType: <b>{branchType || "(missing)"}</b> &nbsp; BranchId(login):{" "}
            <b>{branchIdLogin || "(missing)"}</b>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeLicenseRenewalReport;
